import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './LinkedEvidencePopup.module.css';
import FileItem, { FileData } from '../FileItem/FileItem';
import LoadingIcon from '../LoadingIcon/LoadingIcon';
import Button from '../Button/Button';
import Tooltip from '../Tooltip/Tooltip';
import Popup from '../Popup/Popup';
import AddEvidencePopup from '../AddEvidencePopup/AddEvidencePopup';
import { useAppSelector } from '../../../Redux/Store/Store';
import { getClient, getLibraryGen3Enabled } from '../../../Redux/Store/Slices/client';
import { fetchLibraryFileBytes, getLibraryFileUrl, getLibraryFileUrlByFileId } from '../../../API/Library/Library';
import { deleteEvaluateFileLink } from '../../../API/Evaluate/Evaluate';
import { getConversationFileUrl } from '../../../API/ConversationFiles/ConversationFiles';
import DocumentViewer from '../DocumentViewer/DocumentViewer';
import { LibraryFileRef } from '../../../Types/Library/Library';

interface LinkedEvidencePopupProps {
  files: FileData[];
  fileCount: number;
  title: string;
  error?: string;
  loading?: boolean;
  canLinkEvidence?: boolean;
  roleName?: string;
  areaId?: string;
  onFileLinkRemoved?: () => void;
  /**
   * When set and Gen 3 Library is enabled, Link Evidence opens the portfolio
   * file picker and creates the same FileLink used from the Library file.
   * Otherwise Link Evidence opens the classic Library.
   */
  linkPortfolio?: {
    areaId: string;
    questionId: string;
    linkType: string;
    clientId: string;
    userSub: string;
    subjectNoun?: string;
    onLinked?: () => void;
  };
}

export interface LinkedEvidenceResponse {
  criteriaId: string;
  fileCount: number;
  files: FileData[];
}

const LinkedEvidencePopup: React.FC<LinkedEvidencePopupProps> = ({ 
  files,
  fileCount,
  title,
  error,
  loading,
  canLinkEvidence = false,
  roleName = '',
  areaId,
  onFileLinkRemoved,
  linkPortfolio,
}) => {
  const client = useAppSelector(getClient);
  const libraryGen3Enabled = useAppSelector(getLibraryGen3Enabled);
  const navigate = useNavigate();
  const [addEvidenceOpen, setAddEvidenceOpen] = useState(false);
  const subjectNoun = linkPortfolio?.subjectNoun || 'criteria';
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [removingLinkId, setRemovingLinkId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<{ file: FileData; url: string } | null>(null);

  const unavailableCount = files.filter((file) => file.isAvailable === false).length;

  const handleFileClick = useCallback(async (file: FileData) => {
    if (file.isAvailable === false) {
      return;
    }

    setDownloadError(null);

    if (!client?.id) {
      setDownloadError('Unable to open this file: no client selected.');
      return;
    }

    setDownloadingFileId(file.id);
    try {
      let url: string;

      if (file.portfolioId && file.folderId && file.fileName) {
        // Library FileLink — path-based blob under foliouploads/{portfolio}/{folder}/{name}
        url = await getLibraryFileUrl(
          client.id,
          file.portfolioId,
          file.folderId,
          file.fileName
        );
      } else if (file.conversationId && file.id && file.fileName) {
        // Inline / Evaluate upload — mcp/{conversationId}/{fileId}/{fileName}
        url = await getConversationFileUrl(
          client.id,
          file.conversationId,
          file.id,
          file.fileName
        );
      } else if (file.id) {
        // Linked evidence with a Cosmos file id but no path on the client
        url = await getLibraryFileUrlByFileId(client.id, file.id);
      } else {
        setDownloadError('This file cannot be opened (missing library location).');
        return;
      }

      setViewer({ file, url });
    } catch (err) {
      console.error('Error opening linked evidence:', err);
      setDownloadError('Failed to open file. You may not have access to this library item.');
    } finally {
      setDownloadingFileId(null);
    }
  }, [client?.id]);

  const handleRemoveLink = useCallback(async (file: FileData) => {
    if (!areaId || !file.linkId || !canLinkEvidence) {
      return;
    }

    setRemoveError(null);
    setRemovingLinkId(file.linkId);

    try {
      await deleteEvaluateFileLink(areaId, file.linkId);
      onFileLinkRemoved?.();
    } catch (err) {
      console.error('Error removing file link:', err);
      setRemoveError('Failed to remove link. Please try again.');
    } finally {
      setRemovingLinkId(null);
    }
  }, [areaId, canLinkEvidence, onFileLinkRemoved]);

  const fetchViewerBytes = useCallback(() => {
    if (!client?.id || !viewer?.file.id) {
      return Promise.reject(new Error("Missing file"));
    }
    return fetchLibraryFileBytes(client.id, viewer.file.id);
  }, [client?.id, viewer?.file.id]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitleRow}>
          <h2 className={styles.title}>
            Linked Evidence
            {fileCount > 0 && (
              <span className={styles.fileCount}>
                {fileCount} file{fileCount !== 1 ? 's' : ''}
              </span>
            )}
          </h2>
          <Tooltip text={`Your role as a ${roleName} means that you cannot link evidence`} align={'top'} sideAlign='left' disabled={canLinkEvidence}>
            <Button
              size="Small"
              colour="#FFF"
              textColour="#000"
              border="rgba(0,0,0,0.2)"
              clickHandler={() => {
                if (linkPortfolio && libraryGen3Enabled === true) {
                  setAddEvidenceOpen(true);
                  return;
                }
                navigate('/library');
              }}
              disabled={!canLinkEvidence}
            >
              Link Evidence
            </Button>
          </Tooltip>
        </div>
        {title && (
          <p className={styles.subtitle}>
            For: {title}
          </p>
        )}
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <LoadingIcon />
          </div>
        ) : error ? (
          <div className={styles.errorState}>
            <h3>Error Loading Files</h3>
            <p>{error}</p>
          </div>
        ) : files.length === 0 ? (
          <div className={styles.emptyState}>
            <img src="/img/Resources/empty.svg" alt="No files" />
            <h3>No Linked Evidence</h3>
            <p>
              There are no files linked to this {subjectNoun} yet.<br />
              Use the "Link Evidence" button to add files from your library.
            </p>
          </div>
        ) : (
          <>
            {unavailableCount > 0 && (
              <p className={styles.unavailableBanner} role="status">
                {unavailableCount} linked file{unavailableCount !== 1 ? 's have' : ' has'} been removed
                from the portfolio. Remove the link{unavailableCount !== 1 ? 's' : ''} to clear
                {canLinkEvidence ? ' them' : ' the list'}.
              </p>
            )}
            {downloadError && (
              <p className={styles.downloadError} role="alert">
                {downloadError}
              </p>
            )}
            {removeError && (
              <p className={styles.downloadError} role="alert">
                {removeError}
              </p>
            )}
            <div className={styles.fileList}>
              {files.map((file) => (
                <FileItem
                  key={file.linkId ?? file.id}
                  file={file}
                  onClick={handleFileClick}
                  downloading={downloadingFileId === file.id}
                  onRemove={
                    canLinkEvidence && file.isAvailable === false && file.linkId
                      ? handleRemoveLink
                      : undefined
                  }
                  removing={removingLinkId === file.linkId}
                />
              ))}
            </div>
          </>
        )}
      </div>
      {viewer && client?.id && (
        <DocumentViewer
          fileName={viewer.file.fileName}
          url={viewer.url}
          fetchBytes={fetchViewerBytes}
          onClose={() => setViewer(null)}
          zIndex={100000}
        />
      )}
      {linkPortfolio && libraryGen3Enabled === true && addEvidenceOpen && (
        <Popup isVisible={addEvidenceOpen} onClose={() => setAddEvidenceOpen(false)} zIndex={100001}>
          <AddEvidencePopup
            areaId={linkPortfolio.areaId}
            questionId={linkPortfolio.questionId}
            linkType={linkPortfolio.linkType}
            clientId={linkPortfolio.clientId}
            userSub={linkPortfolio.userSub}
            contextLabel={title}
            subjectNoun={subjectNoun}
            portfolioFilesOnly
            linkedFileRefs={files
              .filter((file) => file.portfolioId && file.folderId && file.fileName)
              .map((file): LibraryFileRef => ({
                portfolioId: file.portfolioId,
                folderId: file.folderId,
                fileName: file.fileName,
                fileId: file.id,
              }))}
            onEvidenceAdded={() => {
              setAddEvidenceOpen(false);
              linkPortfolio.onLinked?.();
            }}
            onClose={() => setAddEvidenceOpen(false)}
          />
        </Popup>
      )}
    </div>
  );
};

export default LinkedEvidencePopup;
